import { useState } from 'react';
import { Link } from 'react-router-dom';
import { auth, ApiRequestError } from '../lib/api.js';
import { Icon } from '../components/Icon.tsx';
import { Button, Card, Field, Notice } from '../components/ui.tsx';
import { useT } from '../i18n/I18n.tsx';
import { LOGIN_LINK_TTL_MINUTES } from '../lib/lifecycle.js';

export function Login() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (state === 'sent') {
    return (
      <div className="page page--narrow">
        <div className="empty">
          <span className="empty__icon">
            <Icon name="mail" size={22} />
          </span>
          <h3>{t('login.sentTitle')}</h3>
          <p>{t('login.sentBody', { minutes: LOGIN_LINK_TTL_MINUTES })}</p>
        </div>
        <div style={{ marginTop: 'var(--s-4)' }}>
          <Notice tone="info" title={t('login.sentNoticeTitle')}>
            {t('login.sentNoticeBody')}
          </Notice>
        </div>
        <div className="row" style={{ marginTop: 'var(--s-4)' }}>
          <Button variant="ghost" icon="arrow-left" onClick={() => setState('idle')}>
            {t('login.useDifferent')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <span className="tile tile--accent" aria-hidden="true">
          <Icon name="mail" size={20} />
        </span>
        <div className="page__title">
          <h1>{t('login.title')}</h1>
          <p>{t('login.subtitle')}</p>
        </div>
      </header>

      <Card>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setState('sending');
            setError(null);
            auth
              .requestLoginLink(email.trim())
              .then(() => setState('sent'))
              .catch((requestError: unknown) => {
                setState('idle');
                setError(
                  requestError instanceof ApiRequestError
                    ? requestError.message
                    : t('login.failed'),
                );
              });
          }}
        >
          <Field label={t('login.emailLabel')} htmlFor="email">
            {({ id }) => (
              <input
                id={id}
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
            )}
          </Field>

          {error ? (
            <div style={{ marginTop: 'var(--s-4)' }}>
              <Notice tone="danger">{error}</Notice>
            </div>
          ) : null}

          <div style={{ marginTop: 'var(--s-5)' }}>
            <Button
              type="submit"
              variant="primary"
              icon="mail"
              block
              busy={state === 'sending'}
              disabled={email.trim().length === 0}
            >
              {t('login.submit')}
            </Button>
          </div>
        </form>
      </Card>

      <div style={{ marginTop: 'var(--s-4)' }}>
        <Notice title={t('login.emailNoticeTitle')}>{t('login.emailNoticeBody')}</Notice>
      </div>

      <p className="text-sm muted" style={{ marginTop: 'var(--s-5)', textAlign: 'center' }}>
        <Link to="/">{t('login.backToStart')}</Link>
      </p>
    </div>
  );
}
